// What a page-feedback submission records about where it was given (page
// feedback, Connor 27 Oct 2026): the element right-clicked (a short CSS-like
// selector path, tag, id, classes, data-* attributes, aria-label / title,
// visible text, bounding box), the click point and viewport, the build, the
// time, and where in the app (route, workspace, tab, project / package, map
// view). Who sent it is added by the server from the session.
//
// No imports and no DOM globals at load: pageFeedback.ts passes everything
// in, and frontend/tests/pageFeedbackContext.test.ts runs this under plain
// Node with fake elements:
//   node --experimental-strip-types --test frontend/tests/pageFeedbackContext.test.ts

/** The parts of an Element this module reads (a real Element satisfies it). */
export interface ElementLike {
  tagName: string;
  id: string;
  classList: { length: number; [i: number]: string } | ArrayLike<string>;
  attributes: ArrayLike<{ name: string; value: string }>;
  parentElement: ElementLike | null;
  children?: ArrayLike<unknown>;
  textContent: string | null;
  getAttribute(name: string): string | null;
  getBoundingClientRect?(): { x: number; y: number; width: number; height: number };
}

export interface MapView {
  zoom: number;
  lat: number;
  lon: number;
  pitch: number;
  bearing: number;
}

export interface FeedbackWhere {
  route: string;
  workspace: string | null;
  workspace_label: string | null;
  tab: string | null;
  tab_label: string | null;
  project_id: string | null;
  package_id: string | null;
  custom_package_id: string | null;
  dataset: string | null;
  selected_asset_id: string | null;
  field_app: boolean;
  map: MapView | null;
}

export interface FeedbackElement {
  selector: string;
  tag: string;
  id: string | null;
  classes: string[];
  data: Record<string, string>;
  aria_label: string | null;
  title: string | null;
  text: string;
  rect: { x: number; y: number; w: number; h: number } | null;
}

export interface FeedbackContext {
  element: FeedbackElement | null;
  click: { x: number; y: number } | null;
  viewport: { w: number; h: number };
  build: { id: string | null; server: string | null };
  client_time: string;
  where: FeedbackWhere;
}

export const TEXT_MAX = 120;
const SELECTOR_DEPTH = 6;
const MAX_CLASSES = 3;
const MAX_DATA_ATTRS = 12;
const DATA_VALUE_MAX = 120;

function classesOf(el: ElementLike): string[] {
  const out: string[] = [];
  const list = el.classList as ArrayLike<string>;
  for (let i = 0; i < list.length; i++) if (list[i]) out.push(list[i]);
  return out;
}

/** A CSS-safe identifier, or null for one a selector can't carry plainly. */
function plainIdent(s: string): string | null {
  return /^[A-Za-z_][\w-]*$/.test(s) ? s : null;
}

/** One step of the path: tag#id, else tag.class1.class2 (generated / long classes skipped). */
export function selectorStep(el: ElementLike): string {
  const tag = el.tagName.toLowerCase();
  const id = el.id ? plainIdent(el.id) : null;
  if (id) return `${tag}#${id}`;
  const cls = classesOf(el).filter((c) => plainIdent(c) && c.length <= 32).slice(0, MAX_CLASSES);
  return cls.length ? `${tag}.${cls.join(".")}` : tag;
}

/** A short path from the nearest ancestor with an id (or up to SELECTOR_DEPTH steps), e.g.
 * "#sidebar > div.card > button.fa-btn". Not guaranteed unique - it is for a person to read. */
export function selectorPath(el: ElementLike | null): string {
  const steps: string[] = [];
  let cur: ElementLike | null = el;
  while (cur && steps.length < SELECTOR_DEPTH) {
    const tag = cur.tagName.toLowerCase();
    if (tag === "html" || tag === "body") break;
    const step = selectorStep(cur);
    steps.unshift(step);
    if (step.includes("#")) break;
    cur = cur.parentElement;
  }
  return steps.join(" > ") || "body";
}

/** Whitespace collapsed and clipped. */
export function visibleText(s: string | null | undefined, max = TEXT_MAX): string {
  const t = (s ?? "").replace(/\s+/g, " ").trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}

export function describeElement(el: ElementLike | null): FeedbackElement | null {
  if (!el) return null;
  const data: Record<string, string> = {};
  const attrs = el.attributes;
  for (let i = 0; i < attrs.length && Object.keys(data).length < MAX_DATA_ATTRS; i++) {
    const a = attrs[i];
    if (a.name.startsWith("data-")) data[a.name.slice(5)] = visibleText(a.value, DATA_VALUE_MAX);
  }
  const r = el.getBoundingClientRect?.();
  return {
    selector: selectorPath(el),
    tag: el.tagName.toLowerCase(),
    id: el.id || null,
    classes: classesOf(el).slice(0, 10),
    data,
    aria_label: el.getAttribute("aria-label"),
    title: el.getAttribute("title"),
    text: visibleText(el.textContent),
    rect: r ? { x: Math.round(r.x), y: Math.round(r.y), w: Math.round(r.width), h: Math.round(r.height) } : null,
  };
}

/** "#project=P-12&tab=x" -> the project id, else null. */
export function projectFromHash(hash: string): string | null {
  const m = /(?:^#|&)project=([^&]+)/.exec(hash || "");
  return m ? decodeURIComponent(m[1]) : null;
}

export function roundView(v: MapView | null): MapView | null {
  if (!v) return null;
  const r = (x: number, d: number) => Math.round(x * 10 ** d) / 10 ** d;
  return { zoom: r(v.zoom, 2), lat: r(v.lat, 5), lon: r(v.lon, 5), pitch: r(v.pitch, 1), bearing: r(v.bearing, 1) };
}

export interface ContextInputs {
  target: ElementLike | null;
  click: { x: number; y: number } | null;
  viewport: { w: number; h: number };
  build: string | null;
  serverBuild: string | null;
  now: Date;
  where: FeedbackWhere;
}

export function buildFeedbackContext(i: ContextInputs): FeedbackContext {
  return {
    element: describeElement(i.target),
    click: i.click ? { x: Math.round(i.click.x), y: Math.round(i.click.y) } : null,
    viewport: { w: Math.round(i.viewport.w), h: Math.round(i.viewport.h) },
    build: { id: i.build, server: i.serverBuild },
    client_time: i.now.toISOString(),
    where: { ...i.where, map: roundView(i.where.map) },
  };
}

// --- the map view, provided by map.ts once the (lazy) full app is loaded ----------
let mapViewProvider: (() => MapView | null) | null = null;

export function setMapViewProvider(fn: (() => MapView | null) | null): void {
  mapViewProvider = fn;
}

export function currentMapView(): MapView | null {
  try {
    return mapViewProvider ? mapViewProvider() : null;
  } catch {
    return null;
  }
}
